import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import laschool from '../images/laschool-logo.svg';
import { useState, useEffect } from 'react';
import { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { getUserFromToken } from '../utils';
import { GettingStartedModal } from './GettingStarted';
import { useUserContext, UserTokenValue } from '../contexts/userContext';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import { useApiService } from '@/hooks/useApiService';

export function Header({ setIsModalOpen }: { setIsModalOpen: (value: boolean) => void }) {
    const [isGettingStartedOpen, setIsGettingStartedOpen] = useState(false);
    const { t } = useTranslation();
    const openGettingStartedModal = () => {
        setIsModalOpen(true);
    };
    const navigate = useNavigate();
    const hideFormationInHeader = useLocation().pathname.includes('version');

    const { user } = useUserContext();

    const [scrollPosition, setScrollPosition] = useState(0);

    const handleScroll = () => {
        setScrollPosition(window.scrollY);
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <>
            <header
                className={`z-40 fixed top-0 flex h-[64px] w-full flex-row items-center justify-between px-5 lg:px-6 transition-all duration-200 ${
                    scrollPosition > 50 ? 'bg-white/75 backdrop-blur-xl lg:px-3' : 'bg-transparent'
                }`}
            >
                <div
                    className={`flex flex-row items-center transition-all duration-200 ${
                        scrollPosition > 50 ? 'scale-90' : 'scale-100'
                    }`}
                >
                    <img
                        className="relative w-[110px] cursor-pointer"
                        src={laschool}
                        alt="Logo"
                        onClick={() => {
                            navigate('/');
                        }}
                    />
                </div>

                {user ? (
                    <div className="flex flex-row items-center gap-3">
                        {!hideFormationInHeader ? (
                            <button
                                className="shadow-sm hover:shadow-blueactive h-[40px] relative cursor-pointer rounded-full bg-gradient-to-b from-[#FFFFFF] to-[#FBFBFB] border border-[#E9E9E9] px-6 py-2 text-center text-base font-semibold text-[#262628] transition-all duration-200 ease-in-out hover:scale-101 active:scale-100 active:shadow-none"
                                onClick={openGettingStartedModal}
                            >
                                {t('school_see_formations')}
                                <GettingStartedModal
                                    isOpen={isGettingStartedOpen}
                                    setIsOpen={setIsGettingStartedOpen}
                                />
                            </button>
                        ) : null}
                        <UserMenu scrollPosition={scrollPosition} />
                    </div>
                ) : (
                    <div className="flex flex-row items-center gap-3">
                        <GettingStartedModal isOpen={isGettingStartedOpen} setIsOpen={setIsGettingStartedOpen} />
                        <button
                            className="shadow-sm hover:shadow-blueactive h-[40px] relative cursor-pointer rounded-full bg-gradient-to-b from-[#FFFFFF] to-[#FBFBFB] border border-[#E9E9E9] px-6 py-2 text-center text-base font-semibold text-[#262628] transition-all duration-200 ease-in-out hover:scale-101 active:scale-100 active:shadow-none"
                            onClick={openGettingStartedModal}
                        >
                            {t('school_connect')}
                        </button>
                    </div>
                )}
            </header>

            <Outlet />
        </>
    );
}

export function UserMenu({ scrollPosition }: { scrollPosition: number }) {
    const navigate = useNavigate();
    const [user, setUser] = useState<UserTokenValue | null>(null);
    const { t } = useTranslation();
    const { logout: wsLogout } = useApiService();
    const { logout: userContextLogout } = useUserContext();

    useEffect(() => {
        const userName = getUserFromToken();
        setUser(userName || null);
    }, []);

    return (
        <Menu as="div" className="relative inline-block text-left">
            <div className="flex flex-row items-center gap-3">
                <Menu.Button
                    className={`flex h-11 w-11 items-center scale-100 justify-center rounded-full border-2 border-white bg-[#DFEAFF] drop-shadow-none transition-all duration-200 ease-in-out cursor-pointer hover:shadow-md active:shadow-sm ${
                        scrollPosition > 50 ? 'scale-90' : 'scale-100'
                    }`}
                >
                    <span className="text-md font-semibold text-[#7FA1E2]">{`${
                        user?.value.firstname?.charAt(0).toUpperCase() || ''
                    }${user?.value.lastname?.charAt(0).toUpperCase() || ''}`}</span>
                </Menu.Button>
            </div>

            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white overflow-hidden shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div>
                        <Menu.Item>
                            {({ active }) => (
                                <a
                                    href="/profile"
                                    className={twMerge(
                                        active ? 'bg-white/60 text-gray-700' : 'text-gray-700',
                                        'block px-4 py-3 text-sm font-semibold'
                                    )}
                                >
                                    {t('school_profile')}
                                </a>
                            )}
                        </Menu.Item>
                    </div>

                    <div>
                        <Menu.Item>
                            {({ active }) => (
                                <a
                                    href="#"
                                    className={twMerge(
                                        active ? 'bg-white/60 text-red-400' : ' text-red-400',
                                        'block px-4 py-3 text-sm font-semibold'
                                    )}
                                    onClick={() => {
                                        userContextLogout();
                                        wsLogout();
                                        navigate('/');
                                    }}
                                >
                                    {t('school_disconnect')}
                                </a>
                            )}
                        </Menu.Item>
                    </div>
                </Menu.Items>
            </Transition>
        </Menu>
    );
}
